import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Flame, Bell, Moon, Sun, HelpCircle, Scroll, Compass,
  Book as BookIcon, Brain, Users, UserCircle, Library, TrendingUp, ArrowUpRight
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import BookDetailsModal from './BookDetailsModal';
import { Book } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { notificationService } from '../services/notificationService';
import { BookCardGrid } from './HomePage';
import StreakCalendarModal from '../components/StreakCalendarModal';
import StreakCongratulationModal from '../components/StreakCongratulationModal';
import { getAllOfflineBookIds } from '../services/offlineStorageService';
import { storageService } from '../services/storageService';
import { motion, AnimatePresence } from 'framer-motion';
import { StudyPlanHomeDashboard } from '../components/StudyPlanHomeDashboard';
import { AnimatedIcon } from '../components/AnimatedIcon';
import { StudyPlanOnboardingModal } from '../components/StudyPlanOnboardingModal';
import { StudyPlanRoadmapModal } from '../components/StudyPlanRoadmapModal';
import { studyPlanService, StudyPlan } from '../services/studyPlanService';
import T from '../components/T';

const formatStreak = (streak: number) => {
  if (streak >= 1000) {
    const k = Math.floor(streak / 1000);
    const rest = streak % 1000;
    return `${k}k${rest > 0 ? rest : ''}`;
  }
  return streak.toString();
};

const LibrituiLogo = ({ className, size = 24 }: { className?: string, size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M22 10l-4-3-4 3" />
    <path d="M14 7l2-2 2 2" />
    <path d="M4 14l8-6 6 4" />
    <path d="M4 14v5a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5" />
    <path d="M12 8v13" />
    <path d="M7 10l5-3 5 3" />
    {/* A stylized bird/book shape to represent the logo */}
    <path d="M12 2L4 6v8l8 4 8-4V6z" opacity="0.1" fill="currentColor" />
  </svg>
);

const BookCover: React.FC<{ book: any, isSmall?: boolean }> = ({ book, isSmall = false }) => {
  const [hasError, setHasError] = useState(false);
  const displayCover = book?.coverUrl || book?.cover;
  const isGenerated = !displayCover || displayCover.startsWith('generated://');

  if (!isGenerated && !hasError) {
    return (
      <div className="w-full h-full relative group overflow-hidden bg-slate-200 dark:bg-slate-800">
        <img
          src={displayCover}
          alt={book?.title || ''}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          onError={() => setHasError(true)}
          loading="lazy"
        />
        {!isSmall && (
          <div className="absolute inset-0 bg-black/50 p-4 pl-[10%] pb-6 flex flex-col justify-end text-left z-20">
            <h4 className="text-white font-medium text-[12px] leading-tight line-clamp-2 drop-shadow-md">
              {book?.title || 'Sem Título'}
            </h4>
            <p className="text-slate-300 font-bold text-[9px] mt-1 line-clamp-1 opacity-90">
              {book?.author || "Autor Desconhecido"}
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`w-full h-full flex flex-col relative overflow-hidden group border border-slate-200/50 dark:border-white/[0.06] ${isSmall ? 'rounded-lg' : 'rounded-[2rem]'} bg-gradient-to-br from-slate-800 to-slate-950`}>
      <div className="absolute -top-10 -left-10 w-32 h-32 bg-primary/5 rounded-full blur-3xl group-hover:bg-primary/10 transition-colors duration-700" />

      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-20 group-hover:scale-110 transition-transform duration-500">
        <BookIcon className="text-primary" size={isSmall ? 48 : 96} />
      </div>

      {isSmall && (
        <div className="relative z-10 flex flex-col h-full justify-center p-4">
          <h4 className="text-[8px] font-normal text-ink line-clamp-4 uppercase font-mono tracking-[0.14em] leading-[1.3] drop-shadow-sm group-hover:text-primary transition-colors">
            {book?.title || 'Sem Título'}
          </h4>
        </div>
      )}

      {!isSmall && (
        <div className="absolute inset-0 bg-black/50 p-4 pl-[10%] pb-6 flex flex-col justify-end text-left z-20">
          <h4 className="text-white font-medium text-[12px] leading-tight line-clamp-2 drop-shadow-md">
            {book?.title || 'Sem Título'}
          </h4>
          <p className="text-slate-300 font-bold text-[9px] mt-1 line-clamp-1 opacity-90">
            {book?.author || "Autor Desconhecido"}
          </p>
        </div>
      )}

      <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/5 to-white/10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
    </div>
  );
};

const HomeOrbMenu: React.FC = () => {
  const navigate = useNavigate();
  const { isDarkMode, toggleAppOnlyTheme, accentColor } = useTheme();
  const { profile, books, refreshBooks } = useData();
  const { user } = useAuth();
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [showStreakModal, setShowStreakModal] = useState(false);
  const [showStreakCongratModal, setShowStreakCongratModal] = useState(false);
  const [offlineBookIds, setOfflineBookIds] = useState<Set<string>>(new Set());
  const [selectedBookForModal, setSelectedBookForModal] = useState<Book | null>(null);
  const [studiedToday, setStudiedToday] = useState(false);

  // Home View Mode: 'study_plan' (Default - Tarefas do Dia, Resumo, Equilíbrio de Habilidades) vs 'bookshelf' (Minha Estante)
  const [homeViewMode, setHomeViewMode] = useState<'study_plan' | 'bookshelf'>('study_plan');
  const [activePlan, setActivePlan] = useState<StudyPlan | null>(null);
  const [isStudyPlanModalOpen, setIsStudyPlanModalOpen] = useState(false);
  const [isRoadmapModalOpen, setIsRoadmapModalOpen] = useState(false);
  const [studyPlanLang, setStudyPlanLang] = useState<string>(() => {
    return localStorage.getItem('libritui_active_study_lang') || 'en';
  });

  const loadActiveStudyPlan = React.useCallback(async (lang: string) => {
    let plan = await studyPlanService.getPlan(user?.id, lang);
    if (!plan && user?.id && user.id !== 'guest') {
      const allPlans = await studyPlanService.getUserPlans(user.id);
      if (allPlans && allPlans.length > 0) {
        plan = allPlans[0];
        setStudyPlanLang(plan.language_code);
        localStorage.setItem('libritui_active_study_lang', plan.language_code);
      }
    }
    setActivePlan(plan);
  }, [user?.id]);

  useEffect(() => {
    loadActiveStudyPlan(studyPlanLang);
  }, [loadActiveStudyPlan, studyPlanLang]);

  useEffect(() => {
    const handlePlanUpdated = (e: any) => {
      if (e?.detail?.plan) {
        setActivePlan(e.detail.plan);
      } else {
        loadActiveStudyPlan(studyPlanLang);
      }
    };
    window.addEventListener('study_plan_updated', handlePlanUpdated);
    return () => window.removeEventListener('study_plan_updated', handlePlanUpdated);
  }, [loadActiveStudyPlan, studyPlanLang]);

  const sortedBooks = React.useMemo(() => {
    if (!books || !Array.isArray(books)) return [];
    return [...books].sort((a, b) => {
      if (a.isFavorite && !b.isFavorite) return -1;
      if (!a.isFavorite && b.isFavorite) return 1;
      return 0;
    });
  }, [books]);

  // Prévia da estante na Home: até 10 livros, misturando em andamento, favoritos e concluídos
  const shelfPreview = React.useMemo(() => {
    const prog = (b: Book): number => {
      const v: any = (b as any).progress;
      if (typeof v === 'number') return v;
      try {
        const raw = localStorage.getItem(`progresso_${b.id}`);
        return raw ? Number(JSON.parse(raw).percent) || 0 : 0;
      } catch { return 0; }
    };
    const picked: Book[] = [];
    const seen = new Set<string>();
    const add = (list: Book[], n: number) => {
      for (const b of list) {
        if (picked.length >= 10 || n <= 0) break;
        if (!seen.has(String(b.id))) { seen.add(String(b.id)); picked.push(b); n--; }
      }
    };
    add(sortedBooks.filter(b => { const v = prog(b); return v > 0 && v < 100; }), 4);
    add(sortedBooks.filter(b => b.isFavorite), 3);
    add(sortedBooks.filter(b => prog(b) >= 100), 3);
    add(sortedBooks, 10);
    return picked;
  }, [sortedBooks]);

  const handleOpenBook = async (book: Book) => {
    setSelectedBookForModal(book);
    
    if (!book.description) {
      try {
        const { cacheManager } = await import('../services/cacheManager');
        let libraryData: any[] = [];
        
        const cached = await cacheManager.getAsync<any>('online_library_metadata_v2');
        if (cached) {
          const parsed = cached.data || cached;
          libraryData = Array.isArray(parsed) ? parsed : [];
        } else {
          const response = await fetch('https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/library-capas-description-level-tags.json');
          if (response.ok) {
            const json = await response.json();
            libraryData = Array.isArray(json) ? json : json.data || [];
            if (libraryData.length > 0) {
              cacheManager.set('online_library_metadata_v2', libraryData);
            }
          }
        }

        if (libraryData && libraryData.length > 0) {
          let extractedId = null;
          if (book.fileUrl && book.fileUrl.match(/\/(\d+)\.(html|pdf)$/i)) {
            extractedId = book.fileUrl.match(/\/(\d+)\.(html|pdf)$/i)![1];
          } else if (book.coverUrl && book.coverUrl.match(/\/(\d+)\.jpg$/i)) {
            extractedId = book.coverUrl.match(/\/(\d+)\.jpg$/i)![1];
          } else if (String(book.id).startsWith('gut-')) {
            const parts = String(book.id).split('-');
            if (parts.length >= 2) extractedId = parts[1];
          } else if (String(book.custom_id).startsWith('gut-')) {
            const parts = String(book.custom_id).split('-');
            if (parts.length >= 2) extractedId = parts[1];
          }
          
          const bookIdToMatch = extractedId || String(book.custom_id || book.id);
          const meta = libraryData.find((item: any) => 
            String(item.id || item.gutenberg_id) === String(bookIdToMatch) || 
            (item.title && book.title && item.title.trim().toLowerCase() === book.title.trim().toLowerCase())
          );
          
          if (meta) {
            const newDesc = meta.description || meta.descricao || "";
            if (newDesc) {
              const enrichedBook = {
                ...book,
                description: newDesc,
                reading_level: meta.reading_level || book.reading_level,
                subjects: meta.subject || meta.subjects || book.subjects,
                language: (meta.language || meta.idioma) ? String(meta.language || meta.idioma).split(/[;,\\s]/)[0].toLowerCase() : book.language
              };
              setSelectedBookForModal(enrichedBook);
              await storageService.saveBook(enrichedBook);
            }
          }
        }
      } catch (e) {
        console.error("Failed to enrich on open", e);
      }
    }
  };

  // Using raw Portuguese text as fallback to avoid i18n keys errors as requested by user constraints
  const { t } = useTranslation();
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return t('common.good_morning', 'Bom dia');
    if (hour >= 12 && hour < 18) return t('common.good_afternoon', 'Boa tarde');
    return t('common.good_night', 'Boa noite');
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  useEffect(() => {
    if (user) {
      notificationService.getUnreadCount(user.id).then(count => setUnreadNotifications(count));
      
      // Check if studied today
      fetch('/api/db', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'select', table: 'streaks', user_id: user.id })
      })
      .then(res => res.json())
      .then(data => {
        if (data && data.data && data.data.length > 0) {
          const streakData = data.data[0];
          const today = new Date().toISOString().substring(0, 10);
          if (streakData.last_computed_date === today) {
            setStudiedToday(true);
          }
        }
      })
      .catch(err => console.error("Failed to check streak", err));
    }
  }, [user]);

  useEffect(() => {
    getAllOfflineBookIds().then(ids => setOfflineBookIds(new Set(ids)));
  }, []);

  const handleToggleCompleted = async (id: string) => {
    const book = books.find((b: any) => b.id === id || b.custom_id === id);
    if (!book) return;
    const isComp = book.status === 'completed' || !!book.isCompleted;
    const nextCompleted = !isComp;

    const updatedBook = {
      ...book,
      status: nextCompleted ? 'completed' : 'reading',
      isCompleted: nextCompleted
    };

    storageService.saveBook(updatedBook);

    if (user && !user.isGuest) {
      try {
        const { dbManager } = await import('../services/db_manager');
        const bookIdToSave = book.custom_id || book.id;
        if (nextCompleted) {
          await dbManager.execute({
            action: 'upsert',
            table: 'user_read_books',
            user_id: user.id,
            data: {
              id: `${user.id}_${bookIdToSave}`,
              user_id: user.id,
              book_id: bookIdToSave,
              language: book.language || 'en',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }
          });
        } else {
          await dbManager.execute({
            action: 'delete_filtered',
            table: 'user_read_books',
            user_id: user.id,
            filters: {
              user_id: user.id,
              book_id: bookIdToSave
            }
          });
        }

        const matchFilter = book.id.includes(':') ? { id: book.id } : { custom_id: book.id };
        await dbManager.from('books', user.id).update({
          status: nextCompleted ? 'completed' : 'reading',
          is_completed: nextCompleted ? 1 : 0
        }, matchFilter).catch(() => {});

        await refreshBooks(true);
      } catch (err) {
        console.error("Failed to toggle completion in D1:", err);
      }
    } else {
      refreshBooks();
    }
  };

  return (
    <div className="relative min-h-screen bg-slate-50 dark:bg-slate-950 pb-24 pt-20 flex flex-col transition-colors duration-500 overflow-x-hidden">
      
      {/* Barra superior (design minimal v1): marca à esquerda, ações em círculos com micro-animações */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-slate-50/80 dark:bg-slate-950/80 backdrop-blur-xl px-5 py-3 flex items-center justify-between transition-colors duration-500">
        <span className="font-sans font-semibold text-[19px] tracking-[-0.04em] text-ink select-none">
          libritui<span className="text-primary">.</span>
        </span>
        <div className="flex items-center gap-1.5 text-ink">
          <button
            onClick={() => setShowStreakCongratModal(true)}
            className="flex items-center gap-1.5 h-9 pl-2.5 pr-3 rounded-full bg-tint-peach text-ink cursor-pointer"
            title={t('streak.title', 'Sequência')}
          >
            <AnimatedIcon motion="pulse" active={studiedToday}>
              <Flame size={16} strokeWidth={1.75} className="text-primary" fill="currentColor" />
            </AnimatedIcon>
            <span className="font-mono text-[12px] tabular-nums">{profile?.current_streak || profile?.streak || 0}</span>
          </button>

          <button
            onClick={() => navigate('/reports')}
            className="w-9 h-9 rounded-full flex items-center justify-center bg-surface hover:bg-tint-stone transition-colors cursor-pointer"
            title={t('common.evolutionReport', 'Relatório de Evolução')}
          >
            <AnimatedIcon motion="draw"><TrendingUp size={17} strokeWidth={1.75} /></AnimatedIcon>
          </button>

          <button
            onClick={() => navigate('/notifications')}
            className="relative w-9 h-9 rounded-full flex items-center justify-center bg-surface hover:bg-tint-stone transition-colors cursor-pointer"
            title={t('notifications.title', 'Notificações')}
          >
            <AnimatedIcon motion="swing" active={unreadNotifications > 0}><Bell size={17} strokeWidth={1.75} /></AnimatedIcon>
            {unreadNotifications > 0 && (
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-ink rounded-full ring-2 ring-surface"></span>
            )}
          </button>

          <button
            onClick={toggleAppOnlyTheme}
            className="w-9 h-9 rounded-full flex items-center justify-center bg-surface hover:bg-tint-stone transition-colors cursor-pointer"
            title={isDarkMode ? t('theme.lightMode', 'Modo Claro') : t('theme.darkMode', 'Modo Escuro')}
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={isDarkMode ? 'sun' : 'moon'}
                initial={{ rotate: -90, scale: 0.6, opacity: 0 }}
                animate={{ rotate: 0, scale: 1, opacity: 1 }}
                exit={{ rotate: 90, scale: 0.6, opacity: 0 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="inline-flex"
              >
                {/* giro suave ao passar o mouse (além da troca animada ao clicar) */}
                <AnimatedIcon motion="spin">
                  {isDarkMode ? <Sun size={17} strokeWidth={1.75} /> : <Moon size={17} strokeWidth={1.75} />}
                </AnimatedIcon>
              </motion.span>
            </AnimatePresence>
          </button>

          <button
            onClick={() => navigate('/help')}
            className="w-9 h-9 rounded-full flex items-center justify-center bg-surface hover:bg-tint-stone transition-colors cursor-pointer"
            title={t('common.help', 'Ajuda')}
          >
            <AnimatedIcon motion="pop"><HelpCircle size={17} strokeWidth={1.75} /></AnimatedIcon>
          </button>
        </div>
      </div>

      {/* Streak Congratulation Modal (Image 1) */}
      <StreakCongratulationModal 
        isOpen={showStreakCongratModal} 
        onClose={() => {
          setShowStreakCongratModal(false);
          setShowStreakModal(true);
        }} 
        streak={profile?.current_streak || profile?.streak || 0} 
      />

      {/* Streak Calendar Modal (Image 3) */}
      <StreakCalendarModal 
        isOpen={showStreakModal} 
        onClose={() => setShowStreakModal(false)} 
        userId={user?.id || ''} 
      />

      {/* Saudação editorial: data em mono, saudação leve, nome em destaque */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-xl mx-auto px-5 mt-6 flex items-end justify-between gap-4"
      >
        <div className="min-w-0">
          <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted mb-3">
            {new Date().toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short' }).replace(/\./g, '')}
          </p>
          <h2 className="font-display text-[38px] leading-[1.02] tracking-[-0.045em] text-ink">
            <span className="font-light">{getGreeting()},</span>
            <br />
            <span className="font-medium truncate block">{profile?.display_name || user?.displayName || ''}<span className="text-primary">.</span></span>
          </h2>
        </div>
        <div className="w-16 h-16 shrink-0 rounded-full p-[3px] ring-1 ring-ink/10 bg-surface">
          <img
            src={profile?.avatar_url || user?.user_metadata?.avatar_url || `https://api.dicebear.com/7.x/notionists/svg?seed=${user?.id}`}
            alt="Profile"
            className="w-full h-full rounded-full object-cover"
            onError={(e) => { e.currentTarget.src = `https://api.dicebear.com/7.x/notionists/svg?seed=${user?.id}`; }}
          />
        </div>
      </motion.div>

      {/* Seletor: Plano de Estudos / Minha Estante, com indicador deslizante */}
      <div className="my-7 px-5 w-full max-w-xl mx-auto">
        <div className="relative grid grid-cols-2 p-1 rounded-full bg-surface border border-black/[0.04] dark:border-white/[0.05]">
          {([['study_plan', 'Plano de Estudos'], ['bookshelf', 'Minha Estante']] as const).map(([mode, label]) => (
            <button
              key={mode}
              onClick={() => setHomeViewMode(mode)}
              className={`relative h-10 rounded-full text-[13px] font-medium cursor-pointer transition-colors duration-300 ${
                homeViewMode === mode ? 'text-surface' : 'text-muted hover:text-ink'
              }`}
            >
              {homeViewMode === mode && (
                <motion.span
                  layoutId="homeViewSwitch"
                  className="absolute inset-0 rounded-full bg-ink"
                  transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                />
              )}
              <span className="relative z-10">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Main Area: Study Plan Dashboard (Default) vs Bookshelf */}
      {homeViewMode === 'study_plan' ? (
        <div className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 pb-12">
          <StudyPlanHomeDashboard
            userId={user?.id}
            activePlan={activePlan}
            onOpenNewPlanModal={() => setIsStudyPlanModalOpen(true)}
            onOpenRoadmapModal={() => setIsRoadmapModalOpen(true)}
            onSelectLanguage={(lang) => {
              setStudyPlanLang(lang);
              localStorage.setItem('libritui_active_study_lang', lang);
            }}
            availableLangs={['en', 'fr', 'it', 'es', 'de', 'ru', 'hu', 'pt']}
          />
        </div>
      ) : (
      <div className="flex-1 w-full max-w-7xl mx-auto pb-10">
        {sortedBooks.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="mx-5 sm:mx-auto max-w-xl rounded-[28px] border border-dashed border-ink/15 bg-surface/50 px-6 py-10 text-center"
          >
            <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-tint-butter text-ink flex items-center justify-center">
              <AnimatedIcon motion="slide" active><Library size={22} strokeWidth={1.6} /></AnimatedIcon>
            </div>
            <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted mb-2">00 livros</p>
            <h3 className="font-display text-2xl font-light tracking-[-0.03em] text-ink"><T k="profile.estante.empty" /><span className="text-primary">.</span></h3>
            <p className="mt-2 text-sm text-muted leading-relaxed max-w-sm mx-auto">
              {t('hubs.library.shelf.emptyPrefix', 'Sua estante está vazia. Não se preocupe! Você pode importar livros gratuitamente da').replace(/^[^.!]*[.!]\s*/, '')} Biblioteca Online.
            </p>
            <button
              onClick={() => navigate('/libritui-library')}
              className="group mt-6 inline-flex items-center gap-3 h-11 pl-5 pr-2 rounded-full bg-primary text-slate-900 text-sm font-medium cursor-pointer hover:opacity-90"
            >
              {t('hubs.library.shelf.emptyLink', 'Biblioteca Online, clique aqui!').replace(/[[\]]/g, '')}
              <span className="w-7 h-7 rounded-full bg-surface/15 flex items-center justify-center transition-transform duration-300 group-hover:rotate-45">
                <ArrowUpRight size={15} strokeWidth={1.75} />
              </span>
            </button>
          </motion.div>
        ) : (
          <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 px-4">
            {shelfPreview.map((book) => (
              <div key={book.id}>
                <BookCardGrid
                  book={book}
                  isSelectionMode={false}
                  isSelected={false}
                  onSelect={() => {}}
                  onDelete={() => {}}
                  onToggleFavorite={() => {}}
                  onToggleCompleted={handleToggleCompleted}
                  onRename={() => {}}
                  onChangeCover={() => {}}
                  onOpen={() => handleOpenBook(book)}
                  onMove={() => {}}
                  isOffline={offlineBookIds.has(book.id)}
                  isDownloading={false}
                  onDownload={() => {}}
                />
              </div>
            ))}
          </div>
          <p className="mt-8 px-4 text-center text-sm text-muted">
            {t('home.shelfPreviewPrefix', 'Confira a sua estante completa em')}{' '}
            <a
              href="/library"
              onClick={(e) => { e.preventDefault(); navigate('/library'); }}
              className="text-primary font-medium underline-offset-4 hover:underline"
            >
              {t('home.shelfPreviewLink', 'Minha Estante')}
            </a>
          </p>
          </>
        )}
      </div>
      )}

      {/* STUDY PLAN MODALS */}
      <StudyPlanOnboardingModal
        isOpen={isStudyPlanModalOpen}
        onClose={() => setIsStudyPlanModalOpen(false)}
        userId={user?.id}
        onPlanCreated={(newPlan, openRoadmap) => {
          setActivePlan(newPlan);
          setStudyPlanLang(newPlan.language_code);
          if (openRoadmap) {
            setIsRoadmapModalOpen(true);
          }
        }}
      />

      {activePlan && (
        <StudyPlanRoadmapModal
          isOpen={isRoadmapModalOpen}
          onClose={() => setIsRoadmapModalOpen(false)}
          plan={activePlan}
        />
      )}

      {selectedBookForModal && (
        <BookDetailsModal
          book={selectedBookForModal}
          onClose={() => setSelectedBookForModal(null)}
          onImport={(b) => {
            navigate(`/reader/${b.id || b.custom_id}`);
            setSelectedBookForModal(null);
          }}
          onPreview={() => {
            setSelectedBookForModal(null);
          }}
          isImporting={false}
          languageFlags={{}}
          renderCover={() => <BookCover book={selectedBookForModal} />}
          primaryLabel={t('library.openBook', 'Abrir Livro')}
          secondaryLabel={""}
          primaryIcon={<BookIcon size={20} />}
          secondaryIcon={<></>}
        />
      )}

    </div>
  );
};

export default HomeOrbMenu;
